import { useState, useEffect } from "react";
import { supabase } from "./lib/supabaseClient";

const ADMIN_USER_ID = import.meta.env.VITE_ADMIN_USER_ID;

const SERVICES = [
  { name: "Vercel", icon: "▲", description: "Deployments, domains, analytics", url: "https://vercel.com/dashboard" },
  { name: "Supabase", icon: "⚡", description: "Database, auth, edge functions", url: "https://supabase.com/dashboard" },
  { name: "GitHub", icon: "🐙", description: "Source code and CI/CD", url: "https://github.com" },
  { name: "Zoho Mail", icon: "📧", description: "Business email", url: "https://mail.zoho.com" },
  { name: "Apple Developer", icon: "🍎", description: "App Store Connect, certificates", url: "https://developer.apple.com" },
  { name: "Google Play Console", icon: "🎮", description: "Play Store listing, releases", url: "https://play.google.com/console" },
  { name: "Google Cloud Console", icon: "☁️", description: "OAuth credentials, APIs", url: "https://console.cloud.google.com" },
  { name: "Sentry", icon: "🐛", description: "Error tracking, session replay", url: "https://sentry.io" },
];

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const checkAuth = (session) => {
      if (!session) {
        setAuthorized(false);
        setDenied(false);
      } else if (session.user.id === ADMIN_USER_ID) {
        setAuthorized(true);
        setDenied(false);
      } else {
        setAuthorized(false);
        setDenied(true);
      }
      setLoading(false);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      checkAuth(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      checkAuth(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin + "/admin" },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-400" />
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-8 max-w-sm w-full text-center">
          <h1 className="text-2xl font-bold text-white mb-2">
            {denied ? "Access Denied" : "Admin Access Required"}
          </h1>
          <p className="text-slate-400 mb-6">
            {denied
              ? "Your account does not have admin privileges."
              : "Sign in with an authorized account to continue."}
          </p>
          {!denied && (
            <button
              onClick={signInWithGoogle}
              className="w-full flex items-center justify-center gap-2 bg-white text-gray-800 font-medium py-3 px-4 rounded-xl hover:bg-gray-100 transition-colors mb-4"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </button>
          )}
          <a href="/" className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
            ← Back to App
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
            Admin Dashboard
          </h1>
          <p className="text-slate-400 mt-1">Fantasy Flashback service links</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((service) => (
            <a
              key={service.name}
              href={service.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-slate-900/80 border border-slate-700/50 rounded-2xl p-5 hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/10 transition-all"
            >
              <div className="text-3xl mb-3">{service.icon}</div>
              <h2 className="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                {service.name}
              </h2>
              <p className="text-sm text-slate-400 mt-1">{service.description}</p>
            </a>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a href="/" className="text-sm text-slate-500 hover:text-slate-300 transition-colors">
            ← Back to App
          </a>
        </div>
      </div>
    </div>
  );
}
